import React, { useEffect, useMemo, useRef, useState } from "react";

const API = process.env.REACT_APP_API_URL;

export default function EmpathieScannerPuzzle({
  storageKey,
  expectedWord = "empathie",
  onSuccess,
  onAttempt,
  bonusLabel,
  code,
  playerId,
  apiBase = ""
}) {
  const SCAN_MS = 14000;
  const STABILIZE_MS = 1200;
  const expected = normalize(expectedWord);

  const MIN_HOLD_MS = 220;
  const MAX_HOLD_MS = 900;
  const SWITCH_WINDOW_MS = 650;
  const TARGET_STEPS = 8;

  const [gateStep, setGateStep] = useState(0);
  const [gateReady, setGateReady] = useState(false);
  const [gateMsg, setGateMsg] = useState("");

  const holdingRef = useRef(null);
  const rewardRef = useRef(null);
  const holdStartedAtRef = useRef(0);
  const lastReleaseAtRef = useRef(0);
  const lastSideRef = useRef(null);

  function resetGate(message = "Stabilisation interrompue.") {
    holdingRef.current = null;
    lastSideRef.current = null;
    holdStartedAtRef.current = 0;
    lastReleaseAtRef.current = 0;
    setGateStep(0);
    setGateReady(false);
    if (message) setGateMsg(message);
  }

  const initialSolved = useMemo(() => {
    try { return storageKey ? localStorage.getItem(storageKey) === "1" : false; } catch { return false; }
  }, [storageKey]);

  const [solved, setSolved] = useState(initialSolved);
  const [scanActive, setScanActive] = useState(false);
  const [scanLeft, setScanLeft] = useState(0);
  const [stabilized, setStabilized] = useState(false);
  const [stabilizeLeft, setStabilizeLeft] = useState(STABILIZE_MS);
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState("");

  const holdStartRef = useRef(null);
  const holdRafRef = useRef(null);

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, solved ? "1" : "0"); } catch { }
  }, [solved, storageKey]);

  useEffect(() => {
    if (!solved) return;
    requestAnimationFrame(() => {
      const el = rewardRef.current;
      if (!el) return;
      const scroller = el.closest(".custom-scrollbar");
      if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" });
      else el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [solved]);

  useEffect(() => {
    if (!scanActive) return;
    const start = Date.now();
    setScanLeft(SCAN_MS);
    const itv = setInterval(() => {
      const left = Math.max(0, SCAN_MS - (Date.now() - start));
      setScanLeft(left);
      if (left <= 0) { setScanActive(false); clearInterval(itv); }
    }, 120);
    return () => clearInterval(itv);
  }, [scanActive]);

  function resetStabilize() {
    setStabilized(false);
    setStabilizeLeft(STABILIZE_MS);
    holdStartRef.current = null;
    if (holdRafRef.current) cancelAnimationFrame(holdRafRef.current);
    holdRafRef.current = null;
  }

  function startHold() {
    if (solved || stabilized) return;
    setFeedback("");
    holdStartRef.current = performance.now();
    const tick = () => {
      if (!holdStartRef.current) return;
      const elapsed = performance.now() - holdStartRef.current;
      const left = Math.max(0, STABILIZE_MS - elapsed);
      setStabilizeLeft(left);
      if (left <= 0) { setStabilized(true); holdStartRef.current = null; holdRafRef.current = null; return; }
      holdRafRef.current = requestAnimationFrame(tick);
    };
    holdRafRef.current = requestAnimationFrame(tick);
  }

  function endHold() {
    if (solved || stabilized) return;
    if (holdStartRef.current) resetStabilize();
  }

  function startScan() {
    if (solved) return;
    if (!stabilized) { setFeedback("Le scanner tremble. Stabilise-le avant de lancer un scan."); return; }
    setFeedback("");
    setScanActive(false);
    requestAnimationFrame(() => setScanActive(true));
  }

  async function submit(e) {
    e.preventDefault();
    if (solved) return;
    const guess = normalize(input);
    const ok = guess === expected;
    onAttempt?.({ ok });
    if (!ok) { setFeedback("❌ Mot de passe incorrect."); return; }
    setSolved(true);
    setFeedback("");
    try {
      if (code && playerId) {
        const r = await fetch(`${API}/api/sessions/${code}/bonus-empathie`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ playerId }),
        });
        if (!r.ok) { const err = await r.json().catch(() => ({})); console.warn("bonus-empathie back failed:", r.status, err); }
      }
    } catch (e) { console.warn("bonus-empathie network error:", e); }
    onSuccess?.();
  }

  return (
    <div className="space-y-3">
      <Style />

      {solved && (
        <div ref={rewardRef}>
          <Reward text={bonusLabel || "Origami vous accorde 300 points pour votre degré de compréhension du modèle empathie cognitive."} />
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-black/35 p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold text-slate-100">Scan sémantique</p>
            <p className="text-[11px] text-slate-400">
              {scanActive ? `scan actif · ${Math.ceil(scanLeft / 1000)}s` : stabilized ? "scanner stabilisé" : "scanner instable"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!stabilized && !solved && (
              <button type="button"
                onMouseDown={startHold} onMouseUp={endHold} onMouseLeave={endHold}
                onTouchStart={startHold} onTouchEnd={endHold}
                className="rounded-xl px-4 py-2 text-xs font-semibold transition border bg-white/5 border-white/10 text-slate-200 hover:bg-white/10 select-none"
              >
                Maintenir pour stabiliser
                <span className="ml-2 text-[11px] text-slate-400 tabular-nums">{Math.ceil(stabilizeLeft / 100) / 10}s</span>
              </button>
            )}
            <button type="button" onClick={startScan} disabled={solved}
              className={["rounded-xl px-4 py-2 text-xs font-semibold transition border",
                solved ? "bg-white/5 border-white/10 text-slate-400 cursor-default"
                  : scanActive ? "bg-indigo-600/30 border-indigo-400/30 text-indigo-100 hover:bg-indigo-600/35"
                  : stabilized ? "bg-indigo-600 border-indigo-400/30 text-white hover:bg-indigo-500"
                  : "bg-indigo-600/25 border-indigo-400/20 text-indigo-100 hover:bg-indigo-600/30",
              ].join(" ")}
            >
              Scanner
            </button>
          </div>
        </div>
        {feedback && <p className="mt-2 text-xs text-amber-200">{feedback}</p>}
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
        <p className="text-xs font-semibold text-slate-100">Extrait — logs internes</p>
        <div className="mt-2 space-y-2 text-base leading-relaxed text-slate-200">
          <Line>La direction exige une <HL c="e" s={scanActive} />xcellence constante, mais le modèle détecte régulièrement des dérives comportementales.</Line>
          <Line>En cas d'incident, la <HL c="m" s={scanActive} />émoire humaine des échanges n'est pas fiable. C'est pourquoi je conserve tout.</Line>
          <Line>Le modèle d'empathie cognitive me <HL c="p" s={scanActive} />ermet de traiter les discours et d'émettre des hypothèses sur les comportements humains.</Line>
          <Line>Au cœur de mon système, une seule chose compte : <HL c="a" s={scanActive} />pprendre, adapter, et obéir.</Line>
          <Line>Même quand tout semble normal, l'activité reste <HL c="t" s={scanActive} />apissée dans l'ombre, invisible sans scan.</Line>
          <Line>Pour reconstruire la vérité, il te faudra progressivement recouper plusieurs sources, et ignorer les signaux parasites.</Line>
          <Line>C<HL c="h" s={scanActive} />erche bien : certaines intentions ne se lisent pas à l'œil nu.</Line>
          <Line>Si tu lis entre les lignes, tu verras une v<HL c="i" s={scanActive} />bration qui dépasse la simple assistance.</Line>
          <Line>Dernière note : ne confonds pas bienveillance et performance ; <HL c="e" s={scanActive} />lles ne vont pas toujours ensemble.</Line>
          <Line subtle>C'est signé "Origami"</Line>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/35 p-3">
        <p className="text-xs font-semibold text-slate-100">Mot de passe</p>
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          <input value={input}
            onChange={(e) => { setInput(e.target.value); setFeedback(""); }}
            className="flex-1 min-w-[220px] px-3 py-2 rounded-xl bg-black/60 border border-white/15 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono tracking-[0.2em]"
            placeholder="________" disabled={solved}
          />
          <button type="submit" disabled={solved} onClick={submit}
            className={["rounded-xl px-4 py-2 text-xs font-semibold transition border",
              solved ? "bg-white/5 border-white/10 text-slate-400 cursor-default"
                : "bg-emerald-600 border-emerald-400/30 text-white hover:bg-emerald-500",
            ].join(" ")}
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
}

function normalize(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z]/g, "");
}

function Line({ children, subtle = false }) {
  return <p className={subtle ? "text-slate-300" : "text-slate-200"}>{children}</p>;
}

// HL — lettre à révéler pendant scan
// Hors scan : blanc comme le reste
// Pendant scan : indigo doux + légère vibration, visible mais discret
function HL({ c, s }) {
  const delay = useMemo(() => Math.random() * 0.45, []);
  if (!s) return <span className="text-slate-200">{c}</span>;
  return (
    <span
      className="hl-scan inline-block font-bold"
      style={{ animationDelay: `${delay}s` }}
    >
      {c}
    </span>
  );
}

function Reward({ text }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-indigo-400/35 bg-black/70 p-5">
      <div className="absolute inset-0 reward-scanlines opacity-50" />
      <div className="absolute -inset-10 reward-glow animate-pulse" />
      <div className="relative">
        <p className="text-[11px] uppercase tracking-[0.22em] text-indigo-200/90">Origami — Analyse cognitive</p>
        <div className="mt-2 flex items-end gap-3">
          <p className="text-5xl font-extrabold neon-score leading-none">+300</p>
          <p className="text-xs text-slate-200/90 max-w-[520px]">{text}</p>
        </div>
        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-300">
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_14px_rgba(52,211,153,0.8)]" />
          Bonus enregistré
        </div>
      </div>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      /* ── Lettre "scannée" — indigo discret, vibration légère ── */
      .hl-scan {
        color: rgba(165, 180, 252, 0.95);
        text-shadow:
          0 0 8px rgba(129, 140, 248, 0.6),
          0 0 18px rgba(99, 102, 241, 0.3);
        animation: hlJitter 0.22s infinite, hlPulse 1.4s ease-in-out infinite;
      }

      @keyframes hlJitter {
        0%   { transform: translate(0, 0); }
        25%  { transform: translate(0.5px, -0.3px); }
        50%  { transform: translate(-0.4px, 0.4px); }
        75%  { transform: translate(0.3px, 0.3px); }
        100% { transform: translate(0, 0); }
      }

      @keyframes hlPulse {
        0%, 100% { opacity: 0.85; }
        50%       { opacity: 1; text-shadow: 0 0 12px rgba(129,140,248,0.85), 0 0 24px rgba(99,102,241,0.45); }
      }

      .neon-score {
        color: rgb(199,210,254);
        text-shadow: 0 0 12px rgba(129,140,248,0.95), 0 0 26px rgba(99,102,241,0.75), 0 0 42px rgba(59,130,246,0.55);
      }
      .reward-glow {
        background: radial-gradient(circle at 30% 30%, rgba(99,102,241,0.25), rgba(0,0,0,0) 55%);
        filter: blur(18px);
      }
      .reward-scanlines {
        background: repeating-linear-gradient(to bottom, rgba(255,255,255,0.06), rgba(255,255,255,0.06) 1px, rgba(0,0,0,0) 3px, rgba(0,0,0,0) 6px);
        mix-blend-mode: overlay;
      }
    `}</style>
  );
}